import { useGetCashFundCheckoutSettingsQuery } from '@apps/guest/routes/CashFunds/CashFunds.graphql.generated';
import type { StripeElementsProviderLazyConfig } from '@shared/core/stripe';
import { useStripeElementsContext } from '@shared/core/stripe';
import React, { useEffect, useMemo, useState } from 'react';

type LoadingCashFundCheckoutSettingsProps = Readonly<{
  cashFundId: string | undefined;
  /** Content to display upon success */
  children: React.ReactNode;
  /** Content to display upon unrecoverable failure */
  fallbackContent?: React.ReactNode;
}>;

export const LoadingCashFundCheckoutSettings: React.FC<LoadingCashFundCheckoutSettingsProps> = ({ children, cashFundId, fallbackContent }) => {
  const { data, error, loading } = useGetCashFundCheckoutSettingsQuery({
    variables: {
      cashFundId: cashFundId ?? '',
    },
    skip: !cashFundId,
    batchMode: 'fast',
  });
  const checkoutSettings = data?.getCashFundCheckoutSettings;

  const lazyConfig = useMemo<StripeElementsProviderLazyConfig | null>(() => (!(error || loading) ? (checkoutSettings ?? null) : null), [checkoutSettings, error, loading]);

  // an intentional decoupling of the data load & render cycles:
  //   described in detail in LoadingRegistryCheckoutSettings (which does the same thing)
  const [configApplied, setConfigApplied] = useState(false);

  const { setLazyConfig } = useStripeElementsContext();
  useEffect(() => {
    if (lazyConfig) {
      setLazyConfig(lazyConfig);
      setConfigApplied(true);
    }
  }, [lazyConfig, setLazyConfig, setConfigApplied]);

  if (error && fallbackContent) {
    // we have reached a final error state
    return <>{fallbackContent}</>;
  }
  if (configApplied) {
    // we have received our critical configuration
    return <>{children}</>;
  }
  // until we are configured, we will not render anything
  return null;
};
